<script setup lang="ts">
import { createEmptyHistoryState, registerHistory } from '@lexical/history'
import { $generateHtmlFromNodes } from '@lexical/html'
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
  REMOVE_LIST_COMMAND,
  registerList,
} from '@lexical/list'
import {
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  INLINE_CODE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  ORDERED_LIST,
  STRIKETHROUGH,
  UNORDERED_LIST,
  registerMarkdownShortcuts,
} from '@lexical/markdown'
import { registerRichText } from '@lexical/rich-text'
import { $findMatchingParent } from '@lexical/utils'
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  createEditor,
} from 'lexical'
import { Code, Lightbulb, List, ListOrdered } from 'lucide-vue-next'
import { onBeforeUnmount, onMounted, reactive, ref } from 'vue'

import AppTooltip from '@/components/AppTooltip.vue'

const COMMENT_TRANSFORMERS = [
  BOLD_ITALIC_STAR, BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR, BOLD_UNDERSCORE,
  ITALIC_STAR, ITALIC_UNDERSCORE,
  STRIKETHROUGH, INLINE_CODE,
  UNORDERED_LIST, ORDERED_LIST,
]

const model = defineModel<string | null>()

const wrapperRef = ref<HTMLElement | null>(null)
let editor: ReturnType<typeof createEditor> | null = null

const activeFormats = reactive({
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  code: false,
  bulletList: false,
  numberedList: false,
})

const btnClass = (active: boolean) =>
  active
    ? 'px-2 py-0.5 rounded text-sm select-none bg-primary/10 text-primary'
    : 'px-2 py-0.5 rounded text-sm select-none text-muted-foreground hover:bg-accent'

onMounted(() => {
  const el = document.createElement('div')
  el.contentEditable = 'true'
  el.style.padding = '0.5rem'
  el.style.minHeight = '96px'
  el.style.outline = 'none'
  wrapperRef.value!.appendChild(el)

  editor = createEditor({
    namespace: 'CommentEditor',
    nodes: [ListNode, ListItemNode],
    onError: (err) => console.error('[LexicalEditor]', err),
    theme: {
      text: {
        bold: 'font-bold',
        italic: 'italic',
        underline: 'underline',
        strikethrough: 'line-through',
        underlineStrikethrough: 'underline line-through',
        code: 'lexical-code',
      },
      list: {
        ul: 'list-disc ml-4',
        ol: 'list-decimal ml-4',
        listitem: 'ml-1',
        nested: { listitem: 'list-none' },
      },
    },
  })

  editor.setRootElement(el)
  registerRichText(editor)
  registerList(editor)
  registerHistory(editor, createEmptyHistoryState(), 300)
  registerMarkdownShortcuts(editor, COMMENT_TRANSFORMERS)

  editor.registerUpdateListener(({ editorState }) => {
    editorState.read(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        activeFormats.bold = selection.hasFormat('bold')
        activeFormats.italic = selection.hasFormat('italic')
        activeFormats.underline = selection.hasFormat('underline')
        activeFormats.strikethrough = selection.hasFormat('strikethrough')
        activeFormats.code = selection.hasFormat('code')

        const anchorNode = selection.anchor.getNode()
        const listNode = $findMatchingParent(anchorNode, (n) => $isListNode(n))
        activeFormats.bulletList = $isListNode(listNode) && listNode.getListType() === 'bullet'
        activeFormats.numberedList = $isListNode(listNode) && listNode.getListType() === 'number'
      } else {
        activeFormats.bulletList = false
        activeFormats.numberedList = false
      }

      const html = $generateHtmlFromNodes(editor!, null)
      model.value = html === '<p><br></p>' || html === '' ? null : html
    })
  })
})

onBeforeUnmount(() => editor?.setRootElement(null))

function formatText(format: 'bold' | 'italic' | 'underline' | 'strikethrough' | 'code') {
  editor?.dispatchCommand(FORMAT_TEXT_COMMAND, format)
}

function toggleList(type: 'bullet' | 'number') {
  if (type === 'bullet') {
    editor?.dispatchCommand(
      activeFormats.bulletList ? REMOVE_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND,
      undefined,
    )
  } else {
    editor?.dispatchCommand(
      activeFormats.numberedList ? REMOVE_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND,
      undefined,
    )
  }
}

function clear() {
  editor?.update(() => $getRoot().clear())
  model.value = null
}

defineExpose({ clear })
</script>

<template>
  <div>
    <!-- Toolbar -->
    <div class="bg-muted border-border flex items-center gap-0.5 rounded-t-md border border-b-0 px-2 py-1">
      <button type="button" :class="btnClass(activeFormats.bold)" title="Bold" @mousedown.prevent="formatText('bold')">
        <strong class="text-xs">B</strong>
      </button>
      <button type="button" :class="btnClass(activeFormats.italic)" title="Italic" @mousedown.prevent="formatText('italic')">
        <em class="text-xs">I</em>
      </button>
      <button type="button" :class="btnClass(activeFormats.underline)" title="Underline" @mousedown.prevent="formatText('underline')">
        <span class="text-xs underline">U</span>
      </button>
      <button type="button" :class="btnClass(activeFormats.strikethrough)" title="Strikethrough" @mousedown.prevent="formatText('strikethrough')">
        <span class="text-xs line-through">S</span>
      </button>
      <button type="button" :class="btnClass(activeFormats.code)" title="Inline Code" @mousedown.prevent="formatText('code')">
        <Code class="size-3.5" />
      </button>

      <div class="bg-border mx-1 h-4 w-px" />

      <button type="button" :class="btnClass(activeFormats.bulletList)" title="Bullet List" @mousedown.prevent="toggleList('bullet')">
        <List class="size-4" />
      </button>
      <button type="button" :class="btnClass(activeFormats.numberedList)" title="Numbered List" @mousedown.prevent="toggleList('number')">
        <ListOrdered class="size-4" />
      </button>

      <div class="bg-border mx-1 h-4 w-px" />

      <!-- Formatting hints tooltip -->
      <AppTooltip side="top" align="end" content-class="lexical-hint-tooltip w-64 p-0 !bg-popover !text-popover-foreground !border" :delay-duration="100">
        <button type="button" class="text-muted-foreground hover:text-foreground rounded p-1 transition-colors">
          <Lightbulb class="size-3.5" />
        </button>
        <template #content>
          <div class="p-3">
            <p class="text-foreground mb-2 text-xs font-semibold">Formatting shortcuts</p>
            <div class="space-y-1.5">
              <div v-for="hint in [
                { label: 'Bold',          preview: 'font-bold',    syntax: '**word**'  },
                { label: 'Italic',        preview: 'italic',       syntax: '_word_'    },
                { label: 'Strikethrough', preview: 'line-through', syntax: '~~word~~'  },
                { label: 'Inline code',   preview: '',             syntax: '`word`',   code: true },
                { label: 'Bullet list',   preview: '',             syntax: '- + space' },
                { label: 'Numbered list', preview: '',             syntax: '1. + space'},
              ]" :key="hint.label" class="flex items-center justify-between gap-3">
                <span :class="['text-muted-foreground text-xs', hint.preview]">{{ hint.label }}</span>
                <span :class="['font-mono text-[11px]', hint.code ? 'lexical-code' : 'text-muted-foreground/60']">{{ hint.syntax }}</span>
              </div>
            </div>
          </div>
        </template>
      </AppTooltip>
    </div>

    <!-- Editor (Vue never manages this div's children) -->
    <div ref="wrapperRef" class="border-border bg-background min-h-24 overflow-hidden rounded-b-md border text-sm p-0" />
  </div>
</template>
